import React, { useState, useContext, useMemo } from 'react';
import _set from 'lodash/set';
import { FlowType, SourceType } from '@common/analytics';
import type {
  HostedButtonDetails,
  OrderDetails,
  HostedButton,
  UcpConfig,
  DeployEnvironment,
  MerchantInfo,
  PaymentPrefillData,
  ButtonVariation,
  MerchantSettings,
  EnableMessagingObj,
  InterstitialExperimentDetails,
  InventoryFromBFS
} from '../typings/Common';
import { CartDetails } from '../typings/Cart';
import { WorldReadyProps } from './WorldReadyContext';
import { Subscription, PlanDetails } from '../components/Subscriptions/types';

interface IData {
  app: {
    cdnHostName: string;
    context?: {
      locality: { country: string; language: string };
    };
    csrfToken: string;
    hostname: string;
    custId: string;
    nonce: string;
    targetEnv: DeployEnvironment;
    worldReady: WorldReadyProps;
    locale: string;
    timeZone: string;
    region: string;
    config: UcpConfig;
  };
  trackingId?: string; // Used for HBS and other flow id purposes
  clientId?: string;
  cartDetails?: CartDetails;
  hostedButtonDetails?: HostedButtonDetails; // From HBS
  orderDetails?: OrderDetails | null;
  hostedButton?: HostedButton; // From BFS
  serverData: {
    csrfToken: string;
    nonce: string;
    sdkUrl: string;
    sdkClientId: string;
    geoAwareDomain?: string;
  };
  // Subscriptions
  subscriptions?: {
    receivableCurrencies?: string[];
    products?: { id: string; name: string }[];
    planDetails?: Partial<PlanDetails>;
    subscriptionsList?: Subscription[];
    payerId?: string;
  };
  isLoading?: boolean;
  paymentPrefillData?: PaymentPrefillData;
  merchantInfo?: MerchantInfo;
  buttonVariation?: ButtonVariation;
  merchantSettings?: MerchantSettings;
  showToggleInfo?: boolean;
  // Flags
  enablePaywall?: boolean;
  enableApplePay?: boolean;
  paypalButtonMessagingEnableData?: EnableMessagingObj;
  enablePaywallButtons?: boolean;
  enableBuyerAddToCart?: boolean;
  enableCrossSell?: boolean;
  interstitial?: InterstitialExperimentDetails;
  is3ButtonsEligible?: boolean;
  isSlowRampMerchant?: boolean;
  shouldDisplayFeedbackButton?: boolean;
  isCartButtonSettingEnabled?: boolean;
  isPayLaterDisabled?: boolean;
  sourcePage?: string;
  // entryPoint
  entryPoint?: {
    flowType: FlowType;
    sourceType: SourceType;
  };
  supportProfileShipTaxConfig?: boolean;
  supportShipTaxOnCustomerSetPrice?: boolean;
  // Cart
  addToCartId?: string;
  accountIsRestricted?: boolean;
  isInventorySupported?: boolean;
  inventory?: InventoryFromBFS;
}

export interface IAppContext extends IData {
  setCustomData: (path: string, value: unknown) => void;
  setCsrfToken: (csrfToken: string) => void;
  setHostedButton: (HostedButton: HostedButton) => void;
  setInventory: (inventory?: InventoryFromBFS) => void;
  setHostedButtonDetails: (hostedButtonDetails: HostedButtonDetails) => void;
}

const AppContext = React.createContext<IAppContext | null>(null);

export const AppContextProvider = ({
  value,
  children
}: {
  value: IData | null;
  children: React.ReactElement;
}) => {
  const [data, setData] = useState(value);

  const appContextValue = useMemo(
    () =>
      data
        ? {
            ...data,
            setCustomData: (path: string, value: unknown) => {
              setData((prevData) => {
                /* istanbul ignore next */
                if (!prevData) return prevData;
                _set(prevData, path, value);
                return {
                  ...prevData
                };
              });
            },
            setCsrfToken: (csrfToken: string) => {
              setData((prevData) => {
                /* istanbul ignore next */
                if (!prevData) return prevData;
                prevData.app.csrfToken = csrfToken;
                return {
                  ...prevData
                };
              });
            },
            setHostedButton: (hostedButton: HostedButton) => {
              setData((prevData) => {
                /* istanbul ignore next */
                if (!prevData) return prevData;
                prevData.hostedButton = hostedButton;
                return {
                  ...prevData
                };
              });
            },
            setInventory: (inventory?: InventoryFromBFS) => {
              setData((prevData) => {
                /* istanbul ignore next */
                if (!prevData) return prevData;
                prevData.inventory = inventory;
                return {
                  ...prevData
                };
              });
            },
            setHostedButtonDetails: (hostedButtonDetails: HostedButtonDetails) => {
              setData((prevData) => {
                /* istanbul ignore next */
                if (!prevData) return prevData;
                prevData.hostedButtonDetails = hostedButtonDetails;
                return {
                  ...prevData
                };
              });
            }
          }
        : null,
    [data]
  );

  return <AppContext.Provider value={appContextValue}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext(AppContext);
